import React, { useEffect, useState } from "react";
import { options } from "@/utils/constants/constants";
import TableHead from "@/components/tables/TableHead";
import UsersTable from "@/components/tables/UsersTable";
import { fetchAllGroups } from "@/services/group.service";
import { fetchAllRoles } from "@/services/role.service";
import { fetchUserAsAdmin } from "@/services/user.service";
import { useDataQueries } from "@/utils/hooks/useDataQueries";
import { getListCacheBootstrap, writeListCache } from "@/utils/listPageCache";

const AdminList = () => {
    const [pageNo, setPageNo, dataLength, setDataLength, sorting, setSorting, filter, setFilter, searchItem, setSearchItem, resetDataQueries] = useDataQueries();
    const listBootstrap = getListCacheBootstrap("admins", { dataLength, sorting, searchItem });

    const [isLoading, setIsLoading] = useState(!listBootstrap.hasCache);
    const [users, setUsers] = useState<any>(listBootstrap.items);
    const [usersLeft, setUsersLeft] = useState(true);
    const [usersHistory, setUsersHistory] = useState<any>(listBootstrap.items);
    const [allFetched, setAllFetched] = useState(false);
    const [dataload, setDataload] = useState(!listBootstrap.hasCache);
    const [groups, setGroups] = React.useState([]);
    const [roles, setRoles] = React.useState([]);
    const [totalCount, setTotalCount] = useState(listBootstrap.totalCount);

    useEffect(() => {
        fetchAllRoles(
            (response: any) => {
                setRoles(response.data.roles);
            },
            (error: any) => {
                console.log(error);
            }
        );
        fetchAllGroups(
            (response: any) => {
                setGroups(response.data.groups);
            },
            (error: any) => {
                console.log(error);
            }
        );
    }, []);

    useEffect(() => {
        const { cacheKey, cached } = getListCacheBootstrap("admins", { dataLength, sorting, searchItem });

        setPageNo(1);
        setAllFetched(false);
        setUsersLeft(true);
        if (cached) {
            setUsers(cached.items);
            setUsersHistory(cached.items);
            setTotalCount(cached.totalCount);
            setDataload(false);
            setIsLoading(false);
        } else {
            setIsLoading(true);
            setDataload(true);
        }

        fetchUserAsAdmin(
            1,
            dataLength,
            sorting,
            filter,
            searchItem,
            (response: any) => {
                const nextUsers = response.data.users ?? [];
                writeListCache(cacheKey, nextUsers, response.data.total_count);
                setDataload(false);
                setIsLoading(false);
                setTotalCount(response.data.total_count);
                if (response.data?.users?.length < dataLength) {
                    setUsersLeft(false);
                    setAllFetched(true);
                }
                setUsers(nextUsers);
                setUsersHistory(nextUsers);
            },
            (error: any) => {
                console.log(error);
                setDataload(false);
                setIsLoading(false);
            }
        );
    }, [dataLength, sorting, searchItem]);

    window.onscroll = function () {
        if (allFetched || isLoading) {
            return;
        }
        if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 1) {
            setPageNo(pageNo + 1);
            setIsLoading(true);
            if (usersLeft) {
                fetchUserAsAdmin(
                    pageNo + 1,
                    dataLength,
                    sorting,
                    filter,
                    searchItem,
                    (response: any) => {
                        setIsLoading(false);
                        setTotalCount(response.data.total_count);
                        if (response.data?.users?.length < dataLength) {
                            setUsersLeft(false);
                            setAllFetched(true);
                        }
                        const r_users = [...users, ...response.data.users];
                        setUsersHistory(r_users);
                        setUsers(r_users);
                    },
                    (error: any) => {
                        console.log(error);
                        setIsLoading(false);
                    }
                );
            }
        }
    };

    const resetResponse = () => {
        setUsersLeft(true);
        setAllFetched(false);
    };

    const reset = () => {
        resetDataQueries?.();
        resetResponse();
    };

    const search = (searchText: string) => {
        setSearchItem(searchText);
    };

    const handleApplyFilter = () => {
        setPageNo(1);
        resetResponse();
        setDataload(true);
        setIsLoading(true);
        fetchUserAsAdmin(
            1,
            dataLength,
            sorting,
            filter,
            searchItem,
            (response: any) => {
                setDataload(false);
                setIsLoading(false);
                setTotalCount(response.data.total_count);
                if (response.data?.users?.length < dataLength) {
                    setUsersLeft(false);
                    setAllFetched(true);
                }
                const users = response.data.users;
                setUsers(users);
                setUsersHistory(users);
            },
            (error: any) => {
                console.log(error);
                setDataload(false);
                setIsLoading(false);
            }
        );
    };

    return (
        <div className="gs-list-page-card">
            <div className="gs-list-page-card-header">
                <TableHead id='admin-table' handleReset={resetResponse} pageNo={pageNo as number} dataLength={dataLength as number} roles={roles} groups={groups} setFilter={setFilter} filter={filter} setDataLength={setDataLength as (dataLength: number) => void} reset={reset} search={search} title="Admins" handleApplyFilter={handleApplyFilter} options={options} currentEnvo={users.length} totalEnvo={totalCount}/>
            </div>
            <div>
                <UsersTable is_allow_manage_shared_access={false} id='admin-table' reset={reset} title="Admins" setUsers={setUsers} search={search} isLoading={isLoading} dataLength={dataLength as number} sorting={sorting} setSorting={setSorting as (sorting: any) => void} searchItem={searchItem as string} allFetched={false} users={users} setPageNo={setPageNo} dataload={dataload} resetDataQueries={resetDataQueries} />
            </div>
        </div>
    );
};

export default AdminList;
